import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Shield, Plus, Loader2, Trash2, CheckCircle2 } from 'lucide-react';

export const ConexionEstableTab = () => {
  const [proxies, setProxies] = useState([]);
  const [proxyForm, setProxyForm] = useState({ name: '', type: 'HTTP', host: '', port: '8080', username: '', password: '', description: '', enabled: true, is_default: false });
  const [proxyDialog, setProxyDialog] = useState(false);
  const [proxyTesting, setProxyTesting] = useState('');
  const [proxyDefaulting, setProxyDefaulting] = useState('');

  useEffect(() => {
    api.get('/whatsapp/proxies').then(r => setProxies(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const createProxy = async () => {
    if (!proxyForm.name || !proxyForm.host || !proxyForm.port) { toast.error('Nombre, host y puerto requeridos'); return; }
    try {
      const r = await api.post('/whatsapp/proxies', proxyForm);
      // El backend desmarca los demás al crear uno predeterminado: reflejarlo en la lista.
      setProxies(p => (r.data?.is_default ? p.map(x => ({ ...x, is_default: false })) : p).concat(r.data));
      setProxyForm({ name: '', type: 'HTTP', host: '', port: '8080', username: '', password: '', description: '', enabled: true, is_default: false });
      setProxyDialog(false);
      toast.success('Proxy creado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Marca un proxy como el predeterminado de la empresa (el backend garantiza la unicidad).
  const setDefaultProxy = async (id) => {
    setProxyDefaulting(id);
    try {
      await api.put(`/whatsapp/proxies/${id}`, { is_default: true, enabled: true });
      setProxies(p => p.map(x => ({ ...x, is_default: x.id === id, enabled: x.id === id ? true : x.enabled })));
      toast.success('Proxy predeterminado actualizado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setProxyDefaulting(''); }
  };

  const deleteProxy = async (id) => {
    try { await api.delete(`/whatsapp/proxies/${id}`); setProxies(p => p.filter(x => x.id !== id)); toast.success('Eliminado'); } catch { toast.error('Error'); }
  };

  const testProxy = async (id) => {
    setProxyTesting(id);
    try {
      const r = await api.post(`/whatsapp/proxies/${id}/test`);
      setProxies(p => p.map(x => x.id === id ? { ...x, test_status: r.data.status, last_test: r.data.tested_at } : x));
      toast[r.data.success ? 'success' : 'error'](r.data.success ? `Proxy OK (IP: ${r.data.ip})` : 'Proxy fallo');
    } catch { toast.error('Error al probar'); }
    finally { setProxyTesting(''); }
  };

  return (
    <div className="space-y-6" data-testid="conexion-estable-tab">
              <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="wa-proxies-card">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Shield className="w-4 h-4 text-orange-400" />Conexión estable de WhatsApp (avanzado)</CardTitle>
                  <CardDescription className="text-muted-foreground text-xs">Solo si tu WhatsApp se desconecta seguido. Ayuda a mantener la sesión estable.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" size="sm" className="border-white/10 text-xs" onClick={() => setProxyDialog(true)} data-testid="add-proxy-btn">
                    <Plus className="w-3 h-3 mr-1" /> Agregar Proxy
                  </Button>
                  {proxies.map(p => (
                    <div key={p.id} className="p-3 rounded-lg bg-black/20 border border-white/5 flex items-center justify-between" data-testid={`proxy-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-white font-medium">{p.name}</span>
                          <Badge variant="outline" className="text-[9px] border-white/10 text-muted-foreground">{p.type}</Badge>
                          <Badge variant="outline" className={`text-[9px] border-white/10 ${p.test_status === 'ok' ? 'text-emerald-400' : p.test_status === 'error' ? 'text-red-400' : 'text-muted-foreground'}`}>
                            {p.test_status === 'ok' ? 'Conectado' : p.test_status === 'error' ? 'Fallido' : 'Sin probar'}
                          </Badge>
                          {p.is_default && (
                            <Badge variant="outline" className="text-[9px] border-orange-400/30 bg-orange-400/10 text-orange-400 flex items-center gap-1" data-testid={`proxy-default-badge-${p.id}`}>
                              <CheckCircle2 className="w-2.5 h-2.5" /> Predeterminado
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground font-mono">{p.host}:{p.port}{p.username ? ` (${p.username})` : ''}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!p.is_default && (
                          <Button variant="ghost" size="sm" className="h-7 text-[10px] text-orange-400" onClick={() => setDefaultProxy(p.id)} disabled={proxyDefaulting === p.id} data-testid={`set-default-proxy-${p.id}`}>
                            {proxyDefaulting === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Predeterminado'}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 text-[10px] text-blue-400" onClick={() => testProxy(p.id)} disabled={proxyTesting === p.id} data-testid={`test-proxy-${p.id}`}>
                          {proxyTesting === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => deleteProxy(p.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {proxies.length === 0 && (
                    <div className="space-y-3">
                      <p className="text-[10px] text-muted-foreground text-center py-2">Sin proxies configurados.</p>
                      <details className="p-4 rounded-lg bg-orange-500/[0.03] border border-orange-500/10" data-testid="proxy-providers-details">
                        <summary className="text-xs font-bold text-orange-400 mb-2 cursor-pointer select-none">Ver proveedores recomendados (avanzado)</summary>
                        <div className="space-y-3 text-[10px] text-muted-foreground mt-2">
                          <div>
                            <p className="font-semibold text-white">Bright Data (Residenciales)</p>
                            <p>1. Crear cuenta en brightdata.com</p>
                            <p>2. Ir a "Residential Proxies" &gt; Crear zona</p>
                            <p>3. Copiar host: <code className="text-orange-400">brd.superproxy.io</code>, puerto: <code className="text-orange-400">22225</code></p>
                            <p>4. Usuario y password de la zona creada</p>
                          </div>
                          <div>
                            <p className="font-semibold text-white">Smartproxy (Datacenter)</p>
                            <p>1. Crear cuenta en smartproxy.com</p>
                            <p>2. Dashboard &gt; "Residential" &gt; Crear sub-usuario</p>
                            <p>3. Host: <code className="text-orange-400">gate.smartproxy.com</code>, puerto: <code className="text-orange-400">7000</code></p>
                            <p>4. Autenticacion con user:pass del sub-usuario</p>
                          </div>
                          <div>
                            <p className="font-semibold text-white">Proxy6 (IPv6 Economico)</p>
                            <p>1. Comprar proxies en proxy6.net (tipo: IPv6, HTTP)</p>
                            <p>2. Copiar IP, puerto, usuario y password del proxy</p>
                            <p>3. Tipo: <code className="text-orange-400">HTTP</code> o <code className="text-orange-400">SOCKS5</code></p>
                          </div>
                          <div>
                            <p className="font-semibold text-white">Oxylabs (Premium)</p>
                            <p>1. Crear cuenta en oxylabs.io</p>
                            <p>2. Dashboard &gt; "Residential Proxies"</p>
                            <p>3. Host: <code className="text-orange-400">pr.oxylabs.io</code>, puerto: <code className="text-orange-400">7777</code></p>
                          </div>
                          <p className="text-[9px] text-orange-400/50 mt-2">Tip: Los proxies residenciales son los mas estables para WhatsApp. Evita proxies datacenter gratuitos.</p>
                        </div>
                      </details>
                    </div>
                  )}
                </CardContent>
              </Card>
      <Dialog open={proxyDialog} onOpenChange={setProxyDialog}>
        <DialogContent className="sm:max-w-md" data-testid="proxy-dialog">
          <DialogHeader><DialogTitle>Agregar Servidor Proxy</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-xs">Nombre</Label>
              <Input data-testid="proxy-name" value={proxyForm.name} onChange={e => setProxyForm({...proxyForm, name: e.target.value})} placeholder="Proxy USA 1" className="bg-black/20 border-white/10 text-xs" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Tipo</Label>
                <Select value={proxyForm.type} onValueChange={v => setProxyForm({...proxyForm, type: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="proxy-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HTTP">HTTP</SelectItem>
                    <SelectItem value="HTTPS">HTTPS</SelectItem>
                    <SelectItem value="SOCKS4">SOCKS4</SelectItem>
                    <SelectItem value="SOCKS5">SOCKS5</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Puerto</Label>
                <Input data-testid="proxy-port" value={proxyForm.port} onChange={e => setProxyForm({...proxyForm, port: e.target.value})} placeholder="8080" className="bg-black/20 border-white/10 text-xs" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Host / IP</Label>
              <Input data-testid="proxy-host" value={proxyForm.host} onChange={e => setProxyForm({...proxyForm, host: e.target.value})} placeholder="proxy.example.com o 192.168.1.1" className="bg-black/20 border-white/10 text-xs font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Usuario (opcional)</Label>
                <Input data-testid="proxy-user" value={proxyForm.username} onChange={e => setProxyForm({...proxyForm, username: e.target.value})} placeholder="usuario" className="bg-black/20 border-white/10 text-xs" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Contrasena (opcional)</Label>
                <Input data-testid="proxy-pass" type="password" value={proxyForm.password} onChange={e => setProxyForm({...proxyForm, password: e.target.value})} placeholder="*****" className="bg-black/20 border-white/10 text-xs" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Descripcion (opcional)</Label>
              <Input data-testid="proxy-desc" value={proxyForm.description} onChange={e => setProxyForm({...proxyForm, description: e.target.value})} placeholder="Proxy residencial USA" className="bg-black/20 border-white/10 text-xs" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
              <div className="min-w-0 pr-3">
                <Label className="text-xs">Predeterminado</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Se usara al conectar o reconectar WhatsApp. Solo uno por empresa.</p>
              </div>
              <Switch checked={proxyForm.is_default} onCheckedChange={v => setProxyForm({...proxyForm, is_default: v})} data-testid="proxy-is-default" />
            </div>
            <Button onClick={createProxy} className="w-full bg-orange-600 hover:bg-orange-500 text-white text-xs" data-testid="save-proxy-btn">
              <Plus className="w-3 h-3 mr-1" /> Crear Proxy
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
